import React, { useEffect, useRef, useState } from 'react';
import {
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  CircularProgress,
  Grid,
  Card,
  CardMedia,
  CardContent,
  SelectChangeEvent,
  Alert,
  Snackbar,
  IconButton,
  Box,
  LinearProgress
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LinkIcon from '@mui/icons-material/Link';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { motion, AnimatePresence } from 'framer-motion';
import { VideoMetadata, VideoQuality } from '../services/api';
import type { ZipProgress } from '../services/mediaContracts';
import {
  buildDownloaderFilename,
  getBestMp4Url,
  getImageExtension,
  sanitizeForFilename,
  toOriginalImageUrl,
} from '../services/mediaUtils';
import { downloadItemsAsZip, downloadMediaUrl, fetchMediaBlob } from '../services/zipDownload';
import { isIOS, mimeForFilename, saveBlob, shareHintMessage } from '../services/fileSave';

// Extend HTMLInputElement to include directory selection attributes
declare module 'react' {
  interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
    webkitdirectory?: string;
    directory?: string;
    mozdirectory?: string;
  }
}

interface VideoInfoProps {
  videoInfo: VideoMetadata;
  videoUrl: string;
  setError: (error: string | null) => void;
  onDelete?: () => void;
}

const getSelectedVideoUrl = (
  video: VideoMetadata['videos'][number],
  quality: VideoQuality
): string => getBestMp4Url(video.variants, video.url, quality);

const VideoInfo: React.FC<VideoInfoProps> = ({ videoInfo, videoUrl, setError: setGlobalError, onDelete }) => {
  const [selectedQuality, setSelectedQuality] = useState<VideoQuality>(
    videoInfo.availableQualities[0] || 'high'
  );
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number>(0);
  const [customFilename, setCustomFilename] = useState<string>('');
  const [activeAction, setActiveAction] = useState<'single' | 'all' | null>(null);
  const [zipProgress, setZipProgress] = useState<ZipProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'warning' | 'error'>('success');
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);

  const loading = activeAction !== null;
  const ios = isIOS();
  const prefetchRef = useRef<{ url: string; blob: Blob } | null>(null);

  // Determine if we're dealing with videos or images
  const hasVideos = videoInfo.videos.length > 0;
  const hasImages = (videoInfo.images?.length || 0) > 0;
  
  // Get the current media item based on the index
  const currentMedia = hasVideos 
    ? videoInfo.videos[selectedVideoIndex]
    : hasImages && videoInfo.images 
      ? videoInfo.images[selectedVideoIndex - (videoInfo.videos.length || 0)]
      : null;

  // Format duration only if it's a video and has duration
  const formattedDuration = hasVideos && currentMedia && 'duration' in currentMedia && typeof currentMedia.duration === 'number'
    ? `${Math.floor(currentMedia.duration / 60)}:${String(Math.floor(currentMedia.duration % 60)).padStart(2, '0')}`
    : null;

  // Get the appropriate thumbnail
  const mediaThumbnail = hasVideos 
    ? (currentMedia as VideoMetadata['videos'][number])?.thumbnail || videoInfo.thumbnail
    : hasImages && videoInfo.images 
      ? (currentMedia as NonNullable<VideoMetadata['images']>[number])?.url || videoInfo.thumbnail
      : videoInfo.thumbnail;

  // Get dimensions if available
  const dimensions = currentMedia?.width && currentMedia?.height
    ? `${currentMedia.width}x${currentMedia.height}`
    : null;

  const totalMediaCount = videoInfo.videos.length + (videoInfo.images?.length || 0);

  const selectedMediaUrl = (() => {
    if (selectedVideoIndex < videoInfo.videos.length) {
      return getSelectedVideoUrl(
        videoInfo.videos[selectedVideoIndex],
        selectedQuality
      );
    }
    const image = videoInfo.images?.[selectedVideoIndex - videoInfo.videos.length];
    return image ? toOriginalImageUrl(image.url) : '';
  })();

  // iOS share() needs transient user activation (~5s). Prefetch the selected
  // file so the download tap can open the share sheet without waiting on CDN.
  useEffect(() => {
    if (!ios || !selectedMediaUrl) return;
    let cancelled = false;
    fetchMediaBlob(selectedMediaUrl)
      .then((blob) => {
        if (!cancelled) prefetchRef.current = { url: selectedMediaUrl, blob };
      })
      .catch(() => {
        /* prefetch is best-effort */
      });
    return () => {
      cancelled = true;
    };
  }, [ios, selectedMediaUrl]);

  const handleQualityChange = (event: SelectChangeEvent) => {
    setSelectedQuality(event.target.value as VideoQuality);
  };

  const handleVideoChange = (event: SelectChangeEvent) => {
    setSelectedVideoIndex(Number(event.target.value));
  };

  const handleFilenameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomFilename(event.target.value);
  };

  const handleDownload = async () => {
    setActiveAction('single');
    setSuccess(null);
    setError(null);
    setGlobalError(null);
    setShowSnackbar(false);

    try {
      const isVideo = selectedVideoIndex < videoInfo.videos.length;
      const mediaUrl = selectedMediaUrl;
      const extension = isVideo ? 'mp4' : getImageExtension(mediaUrl);

      if (!mediaUrl) {
        throw new Error('Media URL not found');
      }

      const usernameMatch = videoUrl.match(/(?:twitter\.com|x\.com)\/(\w+)\/status\//);
      const username = usernameMatch?.[1] || videoInfo.author || 'x';
      const totalMediaCount =
        videoInfo.videos.length + (videoInfo.images?.length || 0);
      let mediaSuffix = '';
      if (totalMediaCount > 1) {
        mediaSuffix = isVideo
          ? `_vid_${selectedVideoIndex + 1}`
          : `_img_${selectedVideoIndex - videoInfo.videos.length + 1}`;
      }

      const filename = buildDownloaderFilename({
        customFilename,
        username,
        title: videoInfo.title,
        extension,
        mediaSuffix,
      });

      const mime = mimeForFilename(filename);
      const cached =
        prefetchRef.current?.url === mediaUrl ? prefetchRef.current.blob : null;
      const method = cached
        ? await saveBlob(cached, filename, mime)
        : await downloadMediaUrl(mediaUrl, filename, mime);
      setSuccess(
        method === 'share'
          ? shareHintMessage(filename)
          : `Media download started: ${filename}`
      );
      setSnackbarSeverity('success');
      setShowSnackbar(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during download';
      setError(errorMessage);
      setGlobalError(errorMessage);
      setSnackbarSeverity('error');
      setShowSnackbar(true);
    } finally {
      setActiveAction(null);
    }
  };

  // Packs every video/image in this post into a single zip.
  const handleDownloadAll = async () => {
    setActiveAction('all');
    setZipProgress(null);
    setSuccess(null);
    setError(null);
    setGlobalError(null);
    setShowSnackbar(false);

    try {
      const zipItems = [
        ...videoInfo.videos.map((video, i) => ({
          url: getSelectedVideoUrl(video, selectedQuality),
          filename: `video_${i + 1}.mp4`,
        })),
        ...(videoInfo.images || []).map((image, i) => {
          const url = toOriginalImageUrl(image.url);
          return {
            url,
            filename: `image_${i + 1}.${getImageExtension(url)}`,
          };
        }),
      ];

      const baseName = customFilename.trim()
        ? sanitizeForFilename(customFilename)
        : sanitizeForFilename(`${videoInfo.author || 'x'}_${videoInfo.tweetId || 'post'}`);
      const zipName = `${baseName}_media.zip`;

      const result = await downloadItemsAsZip(zipItems, zipName, setZipProgress);

      if (result.saved < result.total) {
        setSuccess(`Saved ${result.saved} of ${result.total} files as ${result.zipName}`);
        setSnackbarSeverity('warning');
      } else {
        setSuccess(`Saved ${result.saved} files as ${result.zipName}`);
        setSnackbarSeverity('success');
      }
      setShowSnackbar(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during download';
      setError(errorMessage);
      setGlobalError(errorMessage);
      setSnackbarSeverity('error');
      setShowSnackbar(true);
    } finally {
      setActiveAction(null);
      setZipProgress(null);
    }
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  const handlePrevMedia = () => {
    setSelectedVideoIndex((prev) => (prev > 0 ? prev - 1 : totalMediaCount - 1));
  };

  const handleNextMedia = () => {
    setSelectedVideoIndex((prev) => (prev < totalMediaCount - 1 ? prev + 1 : 0));
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(videoUrl);
    setSuccess('Link copied to clipboard');
    setSnackbarSeverity('success');
    setShowSnackbar(true);
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  return (
    <Paper elevation={3} className="p-4">
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ position: 'relative' }}>
            <Box sx={{ position: 'relative', paddingTop: '56.25%', backgroundColor: '#000000' }}>
              {totalMediaCount > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevMedia}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)'
                      },
                      zIndex: 2
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleNextMedia}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)'
                      },
                      zIndex: 2
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </>
              )}
              <AnimatePresence initial={false} custom={selectedVideoIndex}>
                <motion.div
                  key={selectedVideoIndex}
                  custom={selectedVideoIndex}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 }
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}
                >
                  <CardMedia
                    component="img"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                    image={mediaThumbnail}
                    alt={videoInfo.title}
                  />
                </motion.div>
              </AnimatePresence>
            </Box>
            <CardContent sx={{ 
              p: 2, 
              '&:last-child': { pb: 2 },
              height: 200
            }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {videoInfo.author && (
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 'bold',
                      mb: 0.5
                    }}>
                      {videoInfo.author.startsWith('@') ? (
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.stopPropagation();
                            const username = videoInfo.author?.substring(1);
                            if (username) {
                              window.open(`https://x.com/${username}`, '_blank');
                            }
                          }}
                          sx={{
                            cursor: 'pointer',
                            color: 'inherit',
                            transition: 'color 0.2s ease',
                            '&:hover': {
                              color: 'primary.main'
                            }
                          }}
                        >
                          {videoInfo.author}
                        </Box>
                      ) : videoInfo.author}
                    </Typography>
                  )}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.4
                    }}
                  >
                    {videoInfo.title}
                  </Typography>

                  {/* Spacer to push metadata to bottom */}
                  <Box sx={{ flex: 1 }} />

                  {/* Metadata section */}
                  <Box>
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      mb: 1
                    }}>
                      {formattedDuration && (
                        <Typography variant="body2" color="textSecondary" noWrap>
                          Duration: {formattedDuration}
                        </Typography>
                      )}
                      {dimensions && (
                        <Typography variant="body2" color="textSecondary" noWrap>
                          Resolution: {dimensions}
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Bottom row with date and icons */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 'auto'
                    }}>
                      <Typography variant="caption" color="textSecondary">
                        {new Date().toLocaleString(undefined, {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                          hour12: true
                        })}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton
                          onClick={handleCopyLink}
                          sx={{
                            width: 20,
                            height: 20,
                            padding: 0,
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: 'primary.main'
                            }
                          }}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete?.();
                          }}
                          sx={{
                            width: 16,
                            height: 16,
                            padding: 0,
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: 'error.main'
                            }
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          onClick={() => window.open(videoUrl, '_blank')}
                          sx={{
                            width: 20,
                            height: 20,
                            padding: 0,
                            '&:hover': {
                              backgroundColor: 'transparent'
                            }
                          }}
                        >
                          <img
                            src="/xicon.webp"
                            alt="View on X"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Download Options
            </Typography>
            
            {(videoInfo.videos.length + (videoInfo.images?.length || 0)) > 1 && (
              <FormControl fullWidth margin="normal">
                <InputLabel id="media-select-label">Media</InputLabel>
                <Select
                  labelId="media-select-label"
                  value={selectedVideoIndex.toString()}
                  label="Media"
                  onChange={handleVideoChange}
                >
                  {videoInfo.videos.map((video, index) => (
                    <MenuItem key={`video-${index}`} value={index.toString()}>
                      Video {index + 1} {video.width && video.height ? `(${video.width}x${video.height})` : ''}
                    </MenuItem>
                  ))}
                  {videoInfo.images?.map((image, index) => (
                    <MenuItem key={`image-${index}`} value={(videoInfo.videos.length + index).toString()}>
                      Image {index + 1} {image.width && image.height ? `(${image.width}x${image.height})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {hasVideos && (
              <FormControl fullWidth margin="normal">
                <InputLabel id="quality-select-label">Video Quality</InputLabel>
                <Select
                  labelId="quality-select-label"
                  value={selectedQuality}
                  label="Video Quality"
                  onChange={handleQualityChange}
                >
                  {videoInfo.availableQualities.map((quality) => (
                    <MenuItem key={quality} value={quality}>
                      {quality.charAt(0).toUpperCase() + quality.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              margin="normal"
              label="Custom Filename (optional)"
              variant="outlined"
              value={customFilename}
              onChange={handleFilenameChange}
              placeholder="Enter custom filename (without extension)"
              helperText={
                totalMediaCount > 1
                  ? 'Used for the ZIP name when downloading all'
                  : 'Leave empty to use an auto-generated filename'
              }
            />

            {/* Spacer to push buttons to bottom */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Download Buttons */}
            <Box
              component={motion.div}
              layout
              sx={{
                display: 'flex',
                gap: { xs: 1, sm: 2 },
                justifyContent: 'center',
                mt: 2,
                flexWrap: 'nowrap'
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={activeAction === 'single' ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
                onClick={handleDownload}
                disabled={loading}
                sx={{
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '6px 16px', sm: '8px 22px' },
                  transition: 'opacity 0.2s ease, transform 0.15s ease',
                  '&:active:not(:disabled)': { transform: 'scale(0.98)' }
                }}
              >
                {activeAction === 'single' ? 'Downloading…' : hasVideos ? 'Download Video' : 'Download Image'}
              </Button>

              {totalMediaCount > 1 && (
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{
                    borderColor: 'rgba(29, 155, 240, 0.5)',
                    backgroundColor: 'rgba(29, 155, 240, 0.04)',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(29, 155, 240, 0.1)'
                    },
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '6px 16px', sm: '8px 22px' },
                    transition: 'opacity 0.2s ease, transform 0.15s ease',
                    '&:active:not(:disabled)': { transform: 'scale(0.98)' }
                  }}
                  size="large"
                  startIcon={activeAction === 'all' ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
                  onClick={handleDownloadAll}
                  disabled={loading}
                >
                  {activeAction === 'all'
                    ? zipProgress
                      ? `Packing ${zipProgress.current}/${zipProgress.total}…`
                      : 'Packing…'
                    : `Download All (${totalMediaCount})`}
                </Button>
              )}
            </Box>

            {totalMediaCount > 1 && (
              <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 1 }}>
                Saves all photos & videos from this post as a ZIP
              </Typography>
            )}
            {ios && (
              <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 0.5 }}>
                On iPhone, use Save Video / Save Image in the share sheet to add it to Photos
              </Typography>
            )}
            {hasImages && (
              <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 0.5 }}>
                Images download at original resolution
              </Typography>
            )}

            <AnimatePresence>
              {zipProgress && (
                <motion.div
                  key="zip-progress"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  <Box sx={{ pt: 1.5, px: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ pr: 1, maxWidth: '70%' }}>
                        {zipProgress.label ? `Packing ${zipProgress.label}` : 'Packing…'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {zipProgress.current}/{zipProgress.total}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={zipProgress.total ? (zipProgress.current / zipProgress.total) * 100 : 0}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          transition: 'transform 0.2s ease'
                        }
                      }}
                    />
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default VideoInfo; 